import { useCallback } from 'react'

import { Controller } from 'react-hook-form'

import {
  PrivateMessagingOutgoingContact,
  Member,
  ContactabilityStatus,
  MemberListFilterByOperator,
} from '@tribeplatform/gql-client/types'
import {
  useAddParticipantToGroupChat,
  useAuthMember,
  useCanContactMembers,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Text } from '@tribeplatform/react-ui-kit/Text'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MemberGroups } from '../../CMS/types.js'
import { MemberPicker } from '../../EntityPicker/MemberPicker.js'
import { Form, FormMethods } from '../../Form/index.js'
import { T, useI18n } from '../../i18n/index.js'
import { networkFields } from '../constants.js'

interface FormFields {
  members: Member[]
}

const ADD_MEMBER_LIMIT = 10
const MAX_PARTICIPANTS = 50

interface Props {
  chatId: string
  open: boolean
  onClose: () => void
  membersIds: string[]
  currentParticipantCount?: number
}

export const AddMemberModal = ({
  open,
  onClose,
  chatId,
  currentParticipantCount = 0,
}: Props) => {
  const { $t, formatList } = useI18n()
  const { data: authMember, isModerator } = useAuthMember()
  const { data: network } = useNetwork({ fields: networkFields })
  const { mutateAsync: addParticipants, isLoading } =
    useAddParticipantToGroupChat({
      onSuccess: onClose,
    })
  const { canContactMembers } = useCanContactMembers()
  const { whoCanMembersContact } = network?.settings?.privateMessaging || {}
  const reachableMemberGroup =
    whoCanMembersContact === PrivateMessagingOutgoingContact.Anyone ||
    isModerator
      ? MemberGroups.all
      : MemberGroups.staff

  const onSubmit = useCallback(
    async ({ members }: FormFields, methods?: FormMethods<FormFields>) => {
      try {
        const contactability = await canContactMembers(
          members.map(member => member.id),
        )
        const hasNonContactableMembers = contactability?.data?.filter(
          member => member.status !== ContactabilityStatus.Contactable,
        )
        if (hasNonContactableMembers.length) {
          const names = members
            .filter(member =>
              hasNonContactableMembers.some(
                nonContactableMember =>
                  nonContactableMember.memberId === member.id,
              ),
            )
            .map(member => member.name)
          const formattedNames = formatList(names)
          toast({
            title: $t({
              defaultMessage:
                'You are not allowed to add one or more of the selected members',
              id: 'Messaging.AddMemberModal.CannotContactMembers',
            }),
            description: $t(
              {
                defaultMessage: `Cannot add {names}`,
                id: 'Messaging.AddMemberModal.CannotContactMembers.Description',
              },
              {
                names: formattedNames,
              },
            ),
            status: 'error',
          })
          methods?.setError('members', {})
          return
        }
        await addParticipants({
          chatId,
          input: {
            memberIds: members.map(member => member.id),
          },
        })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Error',
            id: 'Generics.Error',
          }),
          description: $t({
            defaultMessage: 'Failed to add members. Please try again',
            id: 'Messaging.AddMemberModal.FailedToAddMembers.Description',
          }),
          status: 'error',
        })
        methods?.setError('members', {})
      }
    },
    [$t, addParticipants, canContactMembers, chatId, formatList],
  )
  const onMembersFieldChange = useCallback(
    (value: Member[], onFieldChange: (...event: unknown[]) => void) => {
      const onTimeLimitExceeded = value?.length > ADD_MEMBER_LIMIT
      if (onTimeLimitExceeded) {
        toast({
          title: $t(
            {
              defaultMessage: 'You can add up to {max} members at a time.',
              id: 'Messaging.AddMemberModal.MembersField.Description',
            },
            {
              max: ADD_MEMBER_LIMIT,
            },
          ),
          status: 'warning',
        })
        return
      }
      const maxLimitExceeded =
        value?.length + currentParticipantCount > MAX_PARTICIPANTS
      if (maxLimitExceeded) {
        toast({
          title: $t(
            {
              defaultMessage: 'Maximum group size ({max}) reached.',
              id: 'Messaging.AddMemberModal.MembersField.MaxLimitExceeded',
            },
            {
              max: MAX_PARTICIPANTS,
            },
          ),
          status: 'warning',
        })
        return
      }

      onFieldChange(value)
    },
    [currentParticipantCount, $t],
  )

  const membersCountStr = `(${currentParticipantCount}/${MAX_PARTICIPANTS})`

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={
          <div className="flex items-center gap-2">
            <T
              defaultMessage="Add Member"
              id="Messaging.AddMemberModal.Title"
            />
            <Text variant="bodySm" className="text-content-muted">
              {membersCountStr}
            </Text>
          </div>
        }
      />
      <Modal.Content>
        <Form<FormFields>
          className="space-y-6"
          onSubmit={onSubmit}
          defaultValues={{ members: [] }}
        >
          {({ formState: { isDirty }, control }) => (
            <>
              <Controller
                name="members"
                control={control}
                render={({ field }) => (
                  <MemberPicker
                    {...field}
                    noSuggestions
                    multiple
                    value={field.value}
                    onChange={(value: Member[]) =>
                      onMembersFieldChange(value, field.onChange)
                    }
                    placeholder={$t({
                      defaultMessage: 'Search for members',
                      id: 'Messaging.SearchMembers',
                    })}
                    className="w-full"
                    memberGroup={reachableMemberGroup}
                    variables={query => ({
                      filterBy: [
                        {
                          key: 'name',
                          operator: MemberListFilterByOperator.contains,
                          value: JSON.stringify(query),
                        },
                        {
                          key: 'id',
                          operator: MemberListFilterByOperator.not,
                          value: JSON.stringify(authMember.id),
                        },
                      ],
                    })}
                    emptyMessage={$t({
                      defaultMessage: 'No {MEMBERS} found with that name',
                      id: 'Messaging.RecipientNotFound',
                    })}
                  />
                )}
              />
              <Text className="text-sm text-content-muted">
                <T
                  defaultMessage="You can add up to {max} members at a time."
                  id="Messaging.AddMemberModal.MembersField.HelperText"
                  values={{ max: ADD_MEMBER_LIMIT }}
                />
              </Text>
              <Form.Actions>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!isDirty}
                  loading={isLoading}
                >
                  <T defaultMessage="Add" id="Generics.Add" />
                </Button>
              </Form.Actions>
            </>
          )}
        </Form>
      </Modal.Content>
    </Modal>
  )
}
